import React, { useState, useEffect, useCallback } from "react";
import { Plus, ChevronRight } from "lucide-react";
import { Card, PageHeader, Btn, Input, Select, Field, Modal, Table, Spinner, ErrBox } from "../components/UI";
import { FilterBar, buildQS, initFilters } from "../components/FilterBar";
import { fmtRp } from "../utils/format";
import { budgetAPI, masterAPI } from "../utils/api";

const BudgetPage = () => {
  const [data, setData]           = useState([]);
  const [summary, setSummary]     = useState({ total_pagu:0, total_realisasi:0, sisa:0 });
  const [filters, setFilters]     = useState(initFilters());
  const [search, setSearch]       = useState("");
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [err, setErr]             = useState("");
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ tahun_anggaran: new Date().getFullYear(), akun_id:"", coa_detail_id:"", unit_id:"", pagu:"", keterangan:"", kro_id:"" });
  const [refAkun, setRefAkun]   = useState([]);
  const [refUnit, setRefUnit]   = useState([]);
  const [refProgram, setRefProgram] = useState([]);
  const [refKro, setRefKro]     = useState([]);
  const [refCoaDetail, setRefCoaDetail] = useState([]);

  const load = useCallback(async (f = filters, s = search) => {
    setLoading(true); setErr("");
    try {
      const res = await budgetAPI.getAll(buildQS(f, s));
      setData(res.data || []);
      if (res.summary) setSummary(res.summary);
    } catch(e) { setErr(e.message); }
    finally { setLoading(false); }
  }, [filters, search]);

  const loadRefs = async () => {
    try {
      const [akun, unit, opts, cd] = await Promise.all([
        masterAPI.getCoaAkun(),
        masterAPI.getUnit(),
        masterAPI.getCoaOptions(),
        masterAPI.getCoaDetailAkun(),
      ]);
      setRefAkun((akun.data||[]).map(x => ({ value: x.id, label: `${x.kode} - ${x.nama} (${x.jenis})`, jenis: x.jenis })));
      setRefUnit((unit.data||[]).map(x => ({ value: x.id, label: x.nama })));
      setRefProgram((opts.data?.program||[]).map(p => ({ value: p.id, label: `${p.kode} - ${p.nama}` })));
      setRefKro((opts.data?.kro||[]).map(k => ({ value: k.id, label: `${k.kode} - ${k.nama}` })));
      setRefCoaDetail((cd.data||[]).map(x => ({ value: x.id, label: `${x.kode} - ${x.nama}`, akun_id: x.akun_id })));
    } catch(e) { console.error(e); }
  };

  useEffect(() => { load(filters, search); }, [filters, search]);
  useEffect(() => { loadRefs(); }, []);

  const handleSave = async () => {
    if (!form.akun_id || !form.unit_id || !form.pagu) return;
    setSaving(true);
    try {
      await budgetAPI.create({ 
        ...form, 
        pagu: Number(form.pagu),
        kro_id: form.kro_id || null
      });
      setShowModal(false);
      setForm({ tahun_anggaran: new Date().getFullYear(), akun_id:"", coa_detail_id:"", unit_id:"", pagu:"", keterangan:"", kro_id:"" });
      load();
    } catch(e) {
      const msg = e?.response?.data?.message || e.message;
      alert("Gagal menyimpan: " + msg);
    } finally { setSaving(false); }
  };

  const handleActivate = async (id) => {
    try {
      await budgetAPI.update(id, { status: "AKTIF" });
      load();
    } catch(e) { alert("Gagal mengaktifkan: " + e.message); }
  };

  const selectedAkun = refAkun.find(a => a.value === form.akun_id);
  const filteredCoaDetail = form.akun_id ? refCoaDetail.filter(d => String(d.akun_id) === String(form.akun_id)) : [];
  const selectedCoaDetail = refCoaDetail.find(d => d.value === form.coa_detail_id);

  return (
    <div>
      <PageHeader title="RENDAL — Perencanaan Anggaran" subtitle="Pengelolaan pagu dan realisasi anggaran per KRO/Akun MA"
        action={<Btn icon={<Plus size={14}/>} onClick={()=>setShowModal(true)}>Tambah Alokasi</Btn>}
      />
      <FilterBar filters={filters} onChange={setFilters} search={search} onSearch={setSearch}
        searchPlaceholder="Cari akun, program, kegiatan, KRO…"/>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:16, marginBottom:20 }}>
        {[["Total Pagu","#2563EB",summary.total_pagu],["Total Realisasi","#10B981",summary.total_realisasi || summary.total_realized],["Sisa Pagu","#F59E0B",summary.sisa]].map(([l,c,v]) => (
          <Card key={l} style={{ borderLeft:`4px solid ${c}`, padding:"14px 18px" }}>
            <p style={{ margin:"0 0 4px", fontSize:11, fontWeight:600, color:"#6B7280", textTransform:"uppercase" }}>{l}</p>
            <p style={{ margin:0, fontSize:18, fontWeight:700, color:c }}>{fmtRp(v||0)}</p>
            <p style={{ margin:"2px 0 0", fontSize:11, color:"#9CA3AF" }}>Tahun {filters.tahun}</p>
          </Card>
        ))}
      </div>
      <Card>
        {err && <div style={{marginBottom:12}}><ErrBox msg={err} onRetry={()=>load()}/></div>}
        {loading ? <Spinner/> : (
          <Table pageSize={15} cols={[
            { key:"kode_akun", label:"Akun", nowrap:true, render:(v,r)=>(
              <div>
                <span style={{fontFamily:"monospace",fontWeight:700,color:"#2563EB"}}>{v}</span>
                <span style={{marginLeft:4,fontSize:10,padding:"1px 5px",borderRadius:4,background:r.akun_jenis==="BELANJA"?"#FEE2E2":"#D1FAE5",color:r.akun_jenis==="BELANJA"?"#991B1B":"#065F46"}}>{r.akun_jenis}</span>
              </div>
            )},
            { key:"nama_akun", label:"Nama Akun" },
            { key:"program_kode", label:"Program", render:(v,r)=>v?(<span style={{fontSize:11,fontFamily:"monospace",background:"#EEF2FF",padding:"2px 6px",borderRadius:4,color:"#4338CA"}}>{v}</span>):<span style={{color:"#9CA3AF",fontSize:11}}>-</span>},
            { key:"kegiatan_kode", label:"Kegiatan", render:(v,r)=>v?(<span style={{fontSize:11,fontFamily:"monospace",background:"#FEF3C7",padding:"2px 6px",borderRadius:4,color:"#92400E"}}>{v}</span>):<span style={{color:"#9CA3AF",fontSize:11}}>-</span>},
            { key:"kro_kode", label:"KRO", render:(v,r)=>v?(<span style={{fontSize:11,fontFamily:"monospace",background:"#DCFCE7",padding:"2px 6px",borderRadius:4,color:"#166534"}}>{v}</span>):<span style={{color:"#9CA3AF",fontSize:11}}>-</span>},
            { key:"unit", label:"Unit" },
            { key:"tahun_anggaran", label:"Tahun", align:"center" },
            { key:"pagu", label:"Pagu", align:"right", render:v=><span style={{fontFamily:"monospace",fontSize:12}}>{fmtRp(Number(v))}</span> },
            { key:"realized", label:"Realisasi", align:"right", render:v=><span style={{fontFamily:"monospace",fontSize:12}}>{fmtRp(Number(v))}</span> },
            { key:"persen_serapan", label:"Serapan", render:(v,r)=>{
              const pct = Number(r.persen_serapan||0);
              return (
                <div style={{ display:"flex", alignItems:"center", gap:6, minWidth:110 }}>
                  <div style={{ flex:1, height:5, background:"#F1F5F9", borderRadius:3 }}>
                    <div style={{ height:"100%", width:`${Math.min(pct,100)}%`, background:pct>80?"#EF4444":pct>60?"#F59E0B":"#2563EB", borderRadius:3 }}/>
                  </div>
                  <span style={{ fontSize:11, fontWeight:700, minWidth:32 }}>{pct.toFixed(1)}%</span>
                </div>
              );
            }},
            { key:"status", label:"Status", align:"center", render:v=>{
              const colors = { AKTIF:["#ECFDF5","#065F46"], DRAFT_RENDAL:["#FEF3C7","#92400E"], MENUNGGU_SIRUP:["#EDE9FE","#5B21B6"], PROSES_PENGADAAN:["#DBEAFE","#1E40AF"] };
              const [bg,fg] = colors[v] || ["#F3F4F6","#6B7280"];
              return <span style={{fontSize:10,fontWeight:700,padding:"2px 8px",borderRadius:99,background:bg,color:fg}}>{v}</span>;
            }},
            { key:"id", label:"Aksi", align:"center", render:(v,r)=>{
              if (r.status !== "DRAFT" && r.status !== "DRAFT_RENDAL") return <span style={{color:"#9CA3AF",fontSize:11}}>-</span>;
              return (
                <button onClick={()=>{ if(confirm("Aktifkan anggaran ini?")) handleActivate(v); }} style={{fontSize:10,padding:"4px 10px",borderRadius:6,border:"none",background:"#10B981",color:"white",cursor:"pointer",fontWeight:600}}>
                  Aktifkan
                </button>
              );
            }},
          ]} data={data}/>
        )}
      </Card>
      {showModal && (
        <Modal title="Tambah Alokasi Anggaran" onClose={()=>setShowModal(false)} wide>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
            <Field label="Tahun Anggaran" required>
              <Input value={form.tahun_anggaran} onChange={e=>setForm({...form,tahun_anggaran:e.target.value})} type="number"/>
            </Field>
            <Field label="Unit Kerja" required>
              <Select value={form.unit_id} onChange={e=>setForm({...form,unit_id:e.target.value})} options={refUnit} placeholder="Pilih unit…"/>
            </Field>
            <Field label="Program" style={{gridColumn:"1/-1"}}>
              <Select value="" onChange={e=>{}} options={[{value:"",label:"-- Tidak perlu pilih, otomatis dari Akun --"}]} disabled style={{background:"#F9FAFB"}}/>
            </Field>
            <Field label="Akun MA" required style={{gridColumn:"1/-1"}}>
              <Select value={form.akun_id} onChange={e=>setForm({...form,akun_id:e.target.value, coa_detail_id:"", kro_id:""})} options={refAkun} placeholder="Pilih Akun MA…"/>
            </Field>
            {form.akun_id && (
              <Field label="Detail COA" required style={{gridColumn:"1/-1"}}>
                <Select value={form.coa_detail_id} onChange={e=>setForm({...form,coa_detail_id:e.target.value})} options={filteredCoaDetail} placeholder="Pilih Detail COA…"/>
                {selectedCoaDetail && (
                  <div style={{ marginTop:8, padding:8, background:"#EFF6FF", borderRadius:8, fontSize:12 }}>
                    <span style={{fontFamily:"monospace",fontWeight:700,color:"#2563EB"}}>{selectedCoaDetail.label}</span>
                  </div>
                )}
              </Field>
            )}
            {form.akun_id && selectedAkun?.jenis === "BELANJA" && (
              <Field label="KRO (Opsional)" style={{gridColumn:"1/-1"}}>
                <Select value={form.kro_id} onChange={e=>setForm({...form,kro_id:e.target.value})} options={[{value:"",label:"-- Gunakan KRO dari Akun --"}, ...refKro]} placeholder="Pilih KRO override (opsional)…"/>
              </Field>
            )}
            <Field label="Pagu Anggaran" required style={{gridColumn:"1/-1"}}>
              <Input value={form.pagu} onChange={e=>setForm({...form,pagu:e.target.value})} type="number" placeholder="0" style={{fontFamily:"monospace",fontSize:16,fontWeight:700}}/>
            </Field>
            <Field label="Keterangan" style={{gridColumn:"1/-1"}}>
              <Input value={form.keterangan} onChange={e=>setForm({...form,keterangan:e.target.value})} placeholder="Keterangan tambahan (opsional)"/>
            </Field>
          </div>
          <div style={{ display:"flex", gap:8, justifyContent:"flex-end", marginTop:20, paddingTop:16, borderTop:"1px solid #E5E7EB" }}>
            <Btn variant="secondary" onClick={()=>setShowModal(false)}>Batal</Btn>
            <Btn onClick={handleSave} disabled={saving}>{saving?"Menyimpan…":"Simpan Alokasi"}</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default BudgetPage;